import peopleApi from "@/api/people.api"

const formatInvitedPeople = (people) => {
  if (!Array.isArray(people)) return []

  return people.map((user) => {
    return {
      avatar: "",
      email: user.invitedEmail || "",
      fullName:
        user.invitedUserFirstName && user.invitedUserLastName
          ? user.invitedUserFirstName + " " + user.invitedUserLastName
          : "",
      id: "",
      inviteToken: user.token,
      roles: ["invited " + user.role],
    }
  })
}

const formatPeople = (people) => {
  if (!Array.isArray(people)) return []

  return people.map((user) => {
    const basicInfo = user.basic_info || {}
    const djInfo = user.dj_info || {}
    return {
      id: user.id,
      avatar: djInfo.image_url ? process.env.VUE_APP_TOOLS_SERVICE_URL + djInfo.image_url : "",
      fullName: [basicInfo.first_name, basicInfo.last_name].filter(Boolean).join(" "),
      email: basicInfo.email || "",
      roles: user.rights || [],
    }
  })
}

const normalizeAssignment = (assignment) => ({
  scopeType: assignment.scopeType || assignment.scope_type || assignment.entityType || assignment.entity_type,
  scopeKey: assignment.scopeKey || assignment.scope_key || assignment.entityKey || assignment.entity_key,
  role: assignment.role,
})

const formatAccountAccessPerson = (person) => {
  const basicInfo = person.basic_info || person.basicInfo || {}
  const djInfo = person.dj_info || person.djInfo || {}
  let assignments = Array.isArray(person.assignments)
    ? person.assignments.map(normalizeAssignment).filter((assignment) => assignment.scopeType && assignment.role)
    : []
  const inviteToken = person.inviteToken || person.invite_token || person.token || ""

  if (!assignments.length && (person.entityType || person.entity_type) && person.role) {
    assignments = [normalizeAssignment({
      entityType: person.entityType || person.entity_type,
      entityKey: person.entityKey || person.entity_key,
      role: person.role,
    })]
  }

  return {
    id: person.id || "",
    avatar: person.avatar || (djInfo.image_url
      ? process.env.VUE_APP_TOOLS_SERVICE_URL + djInfo.image_url
      : ""),
    fullName:
      person.fullName ||
      person.full_name ||
      [person.invitedUserFirstName, person.invitedUserLastName].filter(Boolean).join(" ") ||
      [basicInfo.first_name, basicInfo.last_name].filter(Boolean).join(" "),
    email: person.email || person.invitedEmail || basicInfo.email || "",
    inviteToken,
    status: inviteToken || ["pending", "invited"].includes(person.status) ? "pending" : "active",
    assignments,
  }
}

const formatLegacyAccountAccess = (people, invites, accountKey) => {
  const active = formatPeople(people).map((person) => ({
    ...person,
    status: "active",
    inviteToken: "",
    assignments: person.roles.map((role) => ({
      scopeType: "account",
      scopeKey: accountKey,
      role: role === "admins" ? "admin" : role,
    })),
  }))
  const pending = formatInvitedPeople(invites).map((person) => ({
    ...person,
    status: "pending",
    assignments: person.roles.map((role) => ({
      scopeType: "account",
      scopeKey: accountKey,
      role: role.replace(/^invited\s+/, "").replace(/^admins$/, "admin"),
    })),
  }))

  return active.concat(pending)
}

export const getAccountAccess = async (accountKey) => {
  try {
    const result = await peopleApi.listAccountAccess(accountKey)
    const people = Array.isArray(result)
      ? result
      : (result.people || result.members || []).concat(result.invites || result.invitations || [])

    return people.map(formatAccountAccessPerson)
  } catch (error) {
    if (!error.response || error.response.status !== 404) throw error

    const [{ admins = [] }, invites] = await Promise.all([
      peopleApi.list("accounts", accountKey),
      peopleApi.getInvited("accounts", accountKey),
    ])

    return formatLegacyAccountAccess(admins, invites, accountKey)
  }
}

export const inviteAccountAccess = async (accountKey, email, assignments) => {
  return peopleApi.inviteAccountAccess(accountKey, email, assignments)
}

export const updateAccountAccess = async (accountKey, person, assignments) => {
  return peopleApi.updateAccountAccess(accountKey, person, assignments)
}

export const revokeAccountAccess = async (accountKey, person) => {
  return peopleApi.revokeAccountAccess(accountKey, person)
}

export const getPortalPeople = async () => {
  let { admins = [] } = await peopleApi.list()

  return formatPeople(admins)
}

export const removePortalUser = async (user) => {
  await peopleApi.removeRole(user, "admins")
}

export const invitePortalUser = async (email) => {
  await peopleApi.sendInvite(email, "admins")
}

export default {
  getPortalPeople,
  removePortalUser,
  invitePortalUser,
  getAccountAccess,
  inviteAccountAccess,
  updateAccountAccess,
  revokeAccountAccess,
}
